import { slugify } from "@/shared/lib/slugify";
import {
  Button,
  FormNumber,
  FormPrice,
  FormRoot,
  Modal,
  RHF,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { AlertTriangleIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Resolver } from "react-hook-form";
import { z } from "zod";
import type { ProductDTO, ProductVariantDTO } from "../../domain/contracts";
import { productsApi } from "../api/client";
import { useGenerateVariants } from "../api/queries";

const GenerateFormSchema = z.object({
  price: z.coerce.number().int().min(0).default(0),
  cost: z.coerce.number().int().min(0).default(0),
  compareAtPrice: z.coerce.number().int().min(0).optional(),
  weightGrams: z.coerce.number().int().min(0).optional(),
  lowStockThreshold: z
    .union([z.coerce.number().int().min(0), z.literal("")])
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
});

type GenerateFormValues = z.infer<typeof GenerateFormSchema>;

function cartesian<T>(arrays: T[][]): T[][] {
  if (arrays.length === 0) return [[]];
  const first = arrays[0];
  if (!first) return [[]];
  const rest = arrays.slice(1);
  const restProduct = cartesian(rest);
  return first.flatMap((item) => restProduct.map((combo) => [item, ...combo]));
}

function getDisplayName(
  value: { lo?: string; en?: string; th?: string } | undefined,
): string {
  if (!value) return "";
  return value.lo || value.en || value.th || "";
}

interface GenerateVariantsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string;
  product: ProductDTO;
  variants: ProductVariantDTO[];
}

export function GenerateVariantsDialog({
  open,
  onOpenChange,
  productId,
  product,
  variants,
}: GenerateVariantsDialogProps) {
  const generateVariants = useGenerateVariants(productId);
  const methods = RHF.useForm<GenerateFormValues>({
    resolver: zodResolver(GenerateFormSchema) as Resolver<GenerateFormValues>,
    defaultValues: {
      price: 0,
      cost: 0,
      compareAtPrice: undefined,
      weightGrams: 250,
      lowStockThreshold: undefined,
    },
  });

  const previewData = useMemo(() => {
    const options = product.options;
    if (!options || options.length === 0)
      return { combinations: [], toCreate: [] };

    const valueSets = options.map((opt) =>
      (opt.values ?? []).map((v) => ({
        optionId: opt.id,
        optionName: getDisplayName(
          opt.name as { lo?: string; en?: string; th?: string },
        ),
        valueId: v.id,
        valueName: getDisplayName(
          v.value as { lo?: string; en?: string; th?: string },
        ),
        valueSlug: slugify(
          getDisplayName(
            v.value as { lo?: string; en?: string; th?: string },
          ) || v.id,
        ),
      })),
    );

    const combinations = cartesian(valueSets);

    const existingCombos = new Set(
      variants.map((v) =>
        JSON.stringify(
          v.optionValues ?? {},
          Object.keys(v.optionValues ?? {}).sort(),
        ),
      ),
    );

    const toCreate = combinations.filter((combo) => {
      const optionValues: Record<string, string> = {};
      for (const item of combo) {
        optionValues[item.optionId] = item.valueId;
      }
      const key = JSON.stringify(
        optionValues,
        Object.keys(optionValues).sort(),
      );
      return !existingCombos.has(key);
    });

    return {
      combinations,
      toCreate,
      skuForCombo: (combo: (typeof valueSets)[0][number][]) => {
        const skuParts = [product.slug, ...combo.map((c) => c.valueSlug)];
        return skuParts.join("-").toUpperCase();
      },
    };
  }, [product.options, product.slug, variants]);

  const newOptions = useMemo(() => {
    const options = product.options;
    if (!options || options.length === 0 || variants.length === 0) return [];
    return options.filter((opt) =>
      variants.some((v) => !v.optionValues?.[opt.id]),
    );
  }, [product.options, variants]);

  const getValueSlug = useCallback(
    (optionId: string, valueId: string): string | null => {
      const opt = product.options?.find((o) => o.id === optionId);
      const val = opt?.values?.find((v) => v.id === valueId);
      if (!val) return null;
      const name = getDisplayName(
        val.value as { lo?: string; en?: string; th?: string },
      );
      return slugify(name || valueId);
    },
    [product.options],
  );

  const skuForOptionValues = useCallback(
    (optionValues: Record<string, string>): string | null => {
      const options = product.options;
      if (!options?.length) return null;
      const parts: string[] = [product.slug];
      for (const opt of options) {
        const valueId = optionValues[opt.id];
        if (!valueId) return null;
        const slug = getValueSlug(opt.id, valueId);
        if (slug == null) return null;
        parts.push(slug);
      }
      return parts.join("-").toUpperCase();
    },
    [product.options, product.slug, getValueSlug],
  );

  const [assignments, setAssignments] = useState<
    Record<string, Record<string, string>>
  >({});

  const setAssignment = useCallback(
    (variantId: string, optionId: string, valueId: string) => {
      setAssignments((prev) => ({
        ...prev,
        [variantId]: {
          ...(prev[variantId] ?? {}),
          [optionId]: valueId,
        },
      }));
    },
    [],
  );

  const applyValueToAll = useCallback(
    (optionId: string, valueId: string) => {
      setAssignments((prev) => {
        const next = { ...prev };
        for (const v of variants) {
          next[v.id] = {
            ...(next[v.id] ?? {}),
            [optionId]: valueId,
          };
        }
        return next;
      });
    },
    [variants],
  );

  const hasExistingVariants = variants.length > 0;
  const hasNoOptions =
    !product.options ||
    product.options.length === 0 ||
    product.options.every((o) => !o.values?.length);
  const toCreateCount = previewData.toCreate.length;
  const queryClient = useQueryClient();

  const variantsListFetching =
    useIsFetching({ queryKey: ["products", productId, "variants"] }) > 0;
  const isRefreshingVariantsList = open && variantsListFetching;

  useEffect(() => {
    if (!open || !productId) return;
    void queryClient.refetchQueries({
      queryKey: ["products", productId, "variants"],
    });
  }, [open, productId, queryClient]);

  const assignmentsComplete =
    newOptions.length === 0 ||
    variants.every((v) =>
      newOptions.every((opt) => assignments[v.id]?.[opt.id]),
    );

  const handleGenerate = async (values: GenerateFormValues) => {
    const body = {
      price: values.price,
      cost: values.cost,
      ...(values.compareAtPrice != null && {
        compareAtPrice: values.compareAtPrice,
      }),
      ...(values.weightGrams != null && { weightGrams: values.weightGrams }),
      ...(values.lowStockThreshold != null && {
        lowStockThreshold: values.lowStockThreshold,
      }),
    };

    if (newOptions.length > 0 && variants.length > 0) {
      if (!assignmentsComplete) {
        toast.error("ກະລຸນາເລືອກ option value ສຳລັບທຸກ variant ກ່ອນ");
        return;
      }
      try {
        for (const v of variants) {
          const patch = assignments[v.id];
          if (!patch || Object.keys(patch).length === 0) continue;
          const merged = { ...(v.optionValues ?? {}), ...patch };
          const sku = skuForOptionValues(merged);
          if (!sku) continue;
          await productsApi.updateVariant(productId, v.id, {
            optionValues: patch,
            sku,
          });
        }
        await queryClient.invalidateQueries({
          queryKey: ["products", productId, "variants"],
        });
        await queryClient.invalidateQueries({
          queryKey: ["products", productId],
        });
      } catch (err: unknown) {
        toast.error("ອັບເດດ variant ລົ້ມເຫຼວ", {
          description: err instanceof Error ? err.message : "ເກີດຂໍ້ຜິດພາດບາງຢ່າງ",
        });
        return;
      }
    }

    generateVariants.mutate(body, {
      onSuccess: (data) => {
        onOpenChange(false);
        if (data.created > 0) {
          methods.reset();
        }
        setAssignments({});
      },
    });
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <div className="max-h-[85vh] space-y-4 overflow-y-auto">
        <h3 className="font-semibold text-lg">ສ້າງ Variants</h3>

        {isRefreshingVariantsList && (
          <p className="text-muted-foreground text-sm">
            ກຳລັງໂຫຼດລາຍການ variants ຫຼ້າສຸດ...
          </p>
        )}

        {hasNoOptions && (
          <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950">
            <AlertTriangleIcon className="size-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div>
              <p className="font-medium text-amber-900 text-sm dark:text-amber-100">
                ບໍ່ມີຕົວເລືອກ
              </p>
              <p className="mt-1 text-amber-700 text-xs dark:text-amber-300">
                ກະລຸນາເລືອກ options ແລະ option values ໃນໜ້າແກ້ໄຂສິນຄ້າກ່ອນ
              </p>
            </div>
          </div>
        )}

        {!isRefreshingVariantsList &&
          hasExistingVariants &&
          toCreateCount > 0 &&
          newOptions.length === 0 && (
            <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950">
              <AlertTriangleIcon className="size-5 shrink-0 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="font-medium text-amber-900 text-sm dark:text-amber-100">
                  ມີ Variants ຢູ່ແລ້ວ
                </p>
                <p className="mt-1 text-amber-700 text-xs dark:text-amber-300">
                  ລະບົບຈະສ້າງພຽງ variants ໃໝ່ຈາກ combinations ທີ່ຍັງບໍ່ມີ (
                  {toCreateCount} ຕົວ)
                </p>
              </div>
            </div>
          )}

        {!isRefreshingVariantsList &&
          newOptions.length > 0 &&
          variants.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-sm">
              ກຳນົດ option ໃໝ່ໃຫ້ variant ທີ່ມີຢູ່
            </h4>
            <p className="text-muted-foreground text-xs">
              ເລືອກ option value ສຳລັບແຕ່ລະ variant ກ່ອນສ້າງ variants ໃໝ່
            </p>
            <div className="max-h-64 overflow-x-auto overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/80">
                  <tr className="border-b">
                    <th className="px-3 py-2 text-left font-medium text-muted-foreground text-xs">
                      SKU / ຕົວເລືອກປັດຈຸບັນ
                    </th>
                    {newOptions.map((opt) => (
                      <th
                        key={opt.id}
                        className="px-3 py-2 text-left font-medium text-muted-foreground text-xs"
                      >
                        <div className="flex flex-col gap-1">
                          <span>
                            {getDisplayName(
                              opt.name as {
                                lo?: string;
                                en?: string;
                                th?: string;
                              },
                            )}
                          </span>
                          <Select
                            value=""
                            onValueChange={(valueId) =>
                              valueId && applyValueToAll(opt.id, valueId)
                            }
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder="ໃຊ້ຄ່າດຽວທຸກແຖວ" />
                            </SelectTrigger>
                            <SelectContent>
                              {(opt.values ?? []).map((val) => (
                                <SelectItem
                                  key={val.id}
                                  value={val.id}
                                  className="text-xs"
                                >
                                  {getDisplayName(
                                    val.value as {
                                      lo?: string;
                                      en?: string;
                                      th?: string;
                                    },
                                  )}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {variants.map((v) => (
                    <tr
                      key={v.id}
                      className="border-b last:border-0 hover:bg-muted/30"
                    >
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          <span className="font-mono text-xs">{v.sku}</span>
                          {v.optionValuesDetails &&
                            v.optionValuesDetails.length > 0 && (
                              <span className="text-muted-foreground text-xs">
                                {v.optionValuesDetails
                                  .map(
                                    (d) =>
                                      getDisplayName(d.valueName) ||
                                      getDisplayName(d.optionName),
                                  )
                                  .join(", ")}
                              </span>
                            )}
                        </div>
                      </td>
                      {newOptions.map((opt) => (
                        <td key={opt.id} className="px-3 py-2">
                          <Select
                            value={assignments[v.id]?.[opt.id] ?? ""}
                            onValueChange={(valueId) =>
                              setAssignment(v.id, opt.id, valueId)
                            }
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder="ເລືອກ" />
                            </SelectTrigger>
                            <SelectContent>
                              {(opt.values ?? []).map((val) => (
                                <SelectItem
                                  key={val.id}
                                  value={val.id}
                                  className="text-xs"
                                >
                                  {getDisplayName(
                                    val.value as {
                                      lo?: string;
                                      en?: string;
                                      th?: string;
                                    },
                                  )}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!hasNoOptions &&
          !isRefreshingVariantsList &&
          (toCreateCount > 0 || newOptions.length > 0) && (
          <FormRoot
            methods={methods as RHF.UseFormReturn<GenerateFormValues>}
            onSubmit={handleGenerate}
          >
            {/* Form: default values */}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormPrice
                name="price"
                label="ລາຄາເລີ່ມຕົ້ນ (ກີບ)"
                currencySymbol="₭"
              />
              <FormPrice
                name="cost"
                label="ຕົ້ນທຶນເລີ່ມຕົ້ນ (ກີບ)"
                currencySymbol="₭"
                placeholder="0"
              />
              <FormPrice
                name="compareAtPrice"
                label="ລາຄາເກົ່າ (ກີບ)"
                currencySymbol="₭"
                placeholder="ບໍ່ລົດ"
              />
              <FormNumber name="weightGrams" label="ນ້ຳໜັກເລີ່ມຕົ້ນ (ກຣາມ)" />
              <FormNumber
                name="lowStockThreshold"
                label="ເກນແຈ້ງເຕືອນສິນຄ້າຄົງໃຈ"
                placeholder="ບໍ່ກຳນົດ"
              />
            </div>

            {/* Preview table: only show when there are combos to create (may be 0 after new-option assign) */}
            {toCreateCount > 0 && (
              <div>
                <h4 className="mb-2 font-medium text-sm">
                  ລາຍການ Variants ທີ່ຈະສ້າງ ({toCreateCount} ຕົວ)
                </h4>
                <div className="max-h-48 overflow-y-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-muted/80">
                      <tr className="border-b">
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground text-xs">
                          ຕົວເລືອກ
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground text-xs">
                          SKU
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.toCreate.map((combo, idx) => (
                        <tr
                          key={
                            previewData.skuForCombo?.(combo) ?? `combo-${idx}`
                          }
                          className="border-b last:border-0"
                        >
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1">
                              {combo.map((item) => (
                                <span
                                  key={`${item.optionId}-${item.valueId}`}
                                  className="rounded bg-muted px-1.5 py-0.5 text-xs"
                                >
                                  {item.optionName}: {item.valueName}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">
                            {previewData.skuForCombo?.(combo) ?? ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={generateVariants.isPending}
              >
                ຍົກເລີກ
              </Button>
              <Button
                type="submit"
                disabled={
                  generateVariants.isPending ||
                  (newOptions.length > 0 && !assignmentsComplete)
                }
              >
                {generateVariants.isPending ? "ກຳລັງສ້າງ..." : "ສ້າງ Variants"}
              </Button>
            </div>
          </FormRoot>
        )}

        {!hasNoOptions &&
          !isRefreshingVariantsList &&
          toCreateCount === 0 && (
          <>
            <p className="text-muted-foreground text-sm">
              ມີ variants ທຸກ combination ແລ້ວ ບໍ່ມີສິ່ງໃໝ່ໃຫ້ສ້າງ
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={generateVariants.isPending}
              >
                ຍົກເລີກ
              </Button>
              <Button disabled>ສ້າງ Variants</Button>
            </div>
          </>
        )}

        {hasNoOptions && (
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={generateVariants.isPending}
            >
              ຍົກເລີກ
            </Button>
            <Button disabled>ສ້າງ Variants</Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
